import { MKUtils } from "./MKUtils";
import { MKHttpRequest } from "../request";
import { MKHttpResponse } from "../../interfaces";
import { ec as EC } from "elliptic";
import * as crypto from "crypto";

// secp256r1 curve
const ecCurve = new EC("p256");

/**
 * Secure Headers manager.
 *
 * This class will manage all tasks related to signing AzukiIMC and DeviceProfile in headers.
 *
 * @internal
 */
export class MKSecureHeadersManager {
    private kssUrl: string;
    private authToken: string;
    private ecKeys: ECKeys;
    private httpRequest: MKHttpRequest;

    constructor(authToken: string, kssUrl: string, publicKeyBase64: string, privateKeyBase64: string) {
        this.kssUrl = kssUrl;
        this.authToken = authToken;
        // ECDH locally stored client keys
        this.ecKeys = {
            publicKey: publicKeyBase64,
            privateKey: privateKeyBase64
        };
        this.httpRequest = new MKHttpRequest();
    }

    /**
     * Generate a new signature for the given request Headers.
     * @param headers the keys value pairs to from the signature.
     * @returns a promise that resolves to a generated signature, else rejects with an error message.
     */
    async getSignatureFromHeaders(headers: Record<string, string>): Promise<any> {
        try {
            if (!this.ecKeys.publicKey || !this.ecKeys.privateKey) {
                throw new Error("Missing EC key pair for signing.");
            }

            const signedKeyBase64 = await this.getSignedKeyFromKSS();
            const signedJsonString = MKUtils.base64Decode(signedKeyBase64.SignedKey);
            const signedjsonObject = JSON.parse(signedJsonString);

            // remove the public and auth token from signedkey
            delete signedjsonObject.PublicKey;
            delete signedjsonObject.AuthorizationToken;

            const updatedSignedJsonString = JSON.stringify(signedjsonObject);
            const signedKey = MKUtils.base64Encode(updatedSignedJsonString);
            const signedJson = this.getSelectedHeadersSignedJson(headers);
            const hash = crypto.createHash("sha256").update(signedJson, "utf8").digest(); // Buffer
            const key = ecCurve.keyFromPrivate(Buffer.from(this.ecKeys.privateKey, 'base64'));
            const signature = key.sign(hash);
            const derSignatureHex = signature.toDER("hex");
            const base64Signature = Buffer.from(derSignatureHex, "hex").toString("base64");

            const resultPayload = {
                signedKey: signedKey,
                signedRequest: {
                    signedJson: signedJson,
                    signature: base64Signature,
                }
            };
            const encoded = MKUtils.base64Encode(MKUtils.jsonStringify(resultPayload));

            return {
                Signature: encoded,
                isWvLicenseEncrypted: signedKeyBase64.WvLicenseEncrypted,
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * KSS signKey request.
     * @returns a promise which will resolve to a string containing the received signedKey and reject with error message in case of failure.
     */
    private async getSignedKeyFromKSS(): Promise<any> {
        if (!this.kssUrl || !this.authToken || !this.ecKeys.publicKey) {
            throw new Error("Invalid input for signKey request!");
        }

        return new Promise<{ SignedKey: string; WvLicenseEncrypted: boolean }>((resolve, reject) => {
            this.httpRequest.post(this.kssUrl, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `OAUTH2 access_token="${this.authToken}"`
                },
                body: {
                    publicKey: this.ecKeys.publicKey
                }
            }).then((response: MKHttpResponse) => {
                if (!response.ok) {
                    return reject(new Error("KSS signKey request failed with HTTP error!"));
                }
                return response.json();
            }).then((data: any) => {
                if (
                    MKUtils.isValidObject(data) &&
                    MKUtils.isValidString(data.SignedKey) &&
                    typeof data.WvLicenseEncrypted === 'boolean'
                ) {
                    return resolve({
                        SignedKey: data.SignedKey,
                        WvLicenseEncrypted: data.WvLicenseEncrypted
                    });
                } else {
                    return reject(new Error("Malformed KSS signKey response!"));
                }
            }).catch((error) => {
                return reject(error);
            });
        });
    }

    /**
     * Get selected headers signedJson string to be used for generating signature.
     * @param headers the keys value pairs to get selected headers
     * @returns a base64 string with the selected headers to be used for generating signature when successful.
     */
    private getSelectedHeadersSignedJson(headers: Record<string, string>): string {
        const selected: Record<string, string> = {};
        if (!headers["AzukiIMC"] && !headers["DeviceProfile"]) {
            throw new Error("Headers IMC and DeviceProfile not found!");
        }

        if (headers["AzukiIMC"]) selected["AzukiIMC"] = headers["AzukiIMC"];
        if (headers["DeviceProfile"]) selected["DeviceProfile"] = headers["DeviceProfile"];

        return MKUtils.base64Encode(MKUtils.jsonStringify(selected));
    }
}

/**
 * ECDH Keys.
 * @internal
 */
interface ECKeys {
    /**
     * Public 65 bytes (uncompressed) key.
     *
     * We send the public key to KSS.
     */
    publicKey: string;

    /**
     * Private 32bytes key.
     *
     * We use the private key to generate signature.
     */
    privateKey: string;
}
